import { useLocalStorage } from '@mantine/hooks'
import { get, isEqual } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'

import {
  SortFilterTraitFilter,
  SortFilterTraitSort,
} from '../../types/sort-filter-trait'
import {
  getInitialFiltersFromSearchParams,
  getInitialSortFromSearchParams,
  isValidSortFilterTraitFilter,
} from '../usePaginatedTableStack/get-initial-values-from-search-params'
import {
  cleanExternalFilters,
  cleanExternalSorts,
} from './clean-external-sort-filter'
import { syncToQueryParams } from './query-param-syncs'
import type { UseSortFilter, UseSortFilterParams } from './use-sort-filter'

const useSortFilter = ({
  initialFilters,
  initialSort,
  localStorageFilterIdentifier,
  sortKeysToFilterAgainst = [],
  filterFieldsToFilterAgainst = [],
  noQueryParamSync = false,
}: UseSortFilterParams): UseSortFilter => {
  // used to reset all filters to initial state after reset
  const [filterResetTimeStamp, setFilterResetTimeStamp] = useState<
    number | null
  >(null)

  const initialSortFromQueryParams = useMemo(() => {
    const queryParamsSort = getInitialSortFromSearchParams()

    return queryParamsSort
  }, [])

  const initialFiltersFromQueryParams = useMemo(() => {
    return getInitialFiltersFromSearchParams()
  }, [])

  const [
    localStorageSortFilter,
    setLocalStorageSortFilter,
    clearLocalStorageFilter,
  ] = useLocalStorage<{
    sort: SortFilterTraitSort[]
    filter: SortFilterTraitFilter[]
  } | null>({
    key: `${localStorageFilterIdentifier}-sort-filter`,
    defaultValue: null,
    getInitialValueInEffect: false,
  })

  const [initialFiltersToUse, setInitialFiltersToUse] = useState<
    SortFilterTraitFilter[]
  >([])
  const [initialSortToUse, setInitialSortToUse] = useState<
    SortFilterTraitSort[]
  >([])

  const [sort, setSort] = useState<SortFilterTraitSort[]>([])
  const [filter, setFilter] = useState<SortFilterTraitFilter[]>([])

  useEffect(() => {
    const sortFilterToSync = {
      sort:
        initialSortFromQueryParams.length > 0 && !noQueryParamSync
          ? initialSortFromQueryParams
          : localStorageSortFilter && localStorageSortFilter.sort?.length > 0
          ? localStorageSortFilter?.sort
          : initialSort,
      filter:
        initialFiltersFromQueryParams.length > 0 && !noQueryParamSync
          ? initialFiltersFromQueryParams
          : localStorageSortFilter && localStorageSortFilter.filter?.length > 0
          ? localStorageSortFilter?.filter
          : initialFilters,
    }

    const usableSortFilterToSync = {
      sort: cleanExternalSorts({
        sortKeysToFilterAgainst,
        sorts: sortFilterToSync.sort,
      }),
      filter: cleanExternalFilters({
        filterFieldsToFilterAgainst,
        filters: sortFilterToSync.filter,
      }).filter(cleanFilter),
    }

    if (!noQueryParamSync) {
      syncToQueryParams({
        valueArray: usableSortFilterToSync.sort,
        paramName: 'sort',
      })
      syncToQueryParams({
        valueArray: usableSortFilterToSync.filter,
        paramName: 'filter',
      })
    }

    setInitialFiltersToUse(usableSortFilterToSync.filter)
    setInitialSortToUse(usableSortFilterToSync.sort)
    setFilter(usableSortFilterToSync.filter)

    setSort(usableSortFilterToSync.sort)
  }, [])

  const setSortWithQuerySync = (sort: SortFilterTraitSort[]): void => {
    setSort(sort)
    if (!noQueryParamSync) {
      syncToQueryParams({ valueArray: sort, paramName: 'sort' })
    }
  }

  const setFiltersWithQuerySync = (filter: SortFilterTraitFilter[]): void => {
    const cleanedFilter = filter.filter(cleanFilter)
    setFilter(cleanedFilter)
    if (!noQueryParamSync) {
      syncToQueryParams({ valueArray: cleanedFilter, paramName: 'filter' })
    }
  }

  const setActiveFilterValue = ({
    field,
    value,
  }: SortFilterTraitFilter): void => {
    const indexToUpdate = filter.findIndex((f) => {
      return f.field === field
    })
    let newFilter: SortFilterTraitFilter[] = [...filter, { field, value }]
    if (indexToUpdate >= 0) {
      const clonedFilters = [...filter]
      clonedFilters[indexToUpdate] = { field, value }
      newFilter = clonedFilters
    }

    setFiltersWithQuerySync(newFilter)
  }

  const saveFilterToLocalStorage = (): void => {
    if (typeof localStorageFilterIdentifier !== 'string') return

    setLocalStorageSortFilter({ sort, filter })
  }

  const resetFilters = (): void => {
    setFiltersWithQuerySync(initialFiltersToUse)
    setSortWithQuerySync(initialSortToUse)
    setFilterResetTimeStamp(Date.now())
  }

  const loadLocalStorageFilter = (): void => {
    if (typeof localStorageFilterIdentifier !== 'string') return

    if (!localStorageSortFilter) return

    setSortWithQuerySync(localStorageSortFilter.sort)
    setFiltersWithQuerySync(localStorageSortFilter.filter)
    setFilterResetTimeStamp(Date.now())
  }

  const activeFilterEqualsLocalStorage = useMemo(() => {
    if (typeof localStorageFilterIdentifier !== 'string') return false

    if (!localStorageSortFilter) return false

    return isEqual(localStorageSortFilter, { sort, filter })
  }, [filter, sort, localStorageSortFilter])

  const activeFilterEqualsInitialFilter = useMemo(() => {
    return isEqual(
      {
        sort: initialSortToUse.sort(),
        filter: initialFiltersToUse.sort().filter(cleanFilter),
      },
      { sort: sort.sort(), filter: filter.sort().filter(cleanFilter) },
    )
  }, [filter, sort, initialFiltersToUse, initialSortToUse])

  return {
    setSort: setSortWithQuerySync,
    sort,
    activeFilters: filter,
    setActiveFilters: setFiltersWithQuerySync,
    setActiveFilterValue,
    resetFilters,
    initialFilters: initialFiltersToUse,
    saveFilterToLocalStorage,
    clearLocalStorageFilter,
    activeFilterEqualsLocalStorage,
    activeFilterEqualsInitialFilter,
    loadLocalStorageFilter,
    filterResetTimeStamp,
    hasValidLocalStorageFilter: isValidSortFilterTraitFilter(
      localStorageSortFilter?.filter,
    ),
  }
}

export default useSortFilter

export const cleanFilter = (filter: SortFilterTraitFilter): boolean => {
  if (typeof filter.value === 'string') {
    return filter.value.trim() !== ''
  }

  if (Array.isArray(filter.value)) {
    return filter.value.length > 0
  }

  if (
    get(filter, 'value.startDate') === null ||
    get(filter, 'value.endDate') === null
  ) {
    return false
  }

  return Boolean(filter.value)
}
